import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';

// ============================================
// DOCUMENTATION ARTICLES CONTENT
// ============================================

const ARTICLES = {
  // Getting Started
  'quick-start': {
    title: 'Quick Start Guide',
    category: 'Getting Started',
    readTime: '5 min',
    content: `
## Welcome to Edrano

This guide will help you set up your client portal in under 10 minutes.

### Step 1: Create Your Account

1. Visit [app.edrano.my/register](https://app.edrano.my/register)
2. Enter your business email and create a secure password
3. Fill in your company name and phone number
4. Click **Create Account**

You'll receive a confirmation email. Click the link to verify your account.

### Step 2: Configure Your Portal

After logging in, you'll land on your admin dashboard. Here's what to do next:

1. **Go to Settings → Portal Settings**
2. Upload your logo (recommended: 200x50px PNG)
3. Set your primary brand color
4. Enter your business name and tagline
5. Click **Save Changes**

### Step 3: Add Your First Service

1. Navigate to **Services → Add Service**
2. Choose a category (or create a new one)
3. Enter the service name and description
4. Set up pricing tiers (monthly, yearly, one-time)
5. List the features included
6. Click **Create Service**

### Step 4: Set Up Payments

1. Go to **Settings → Payment Settings**
2. Click **Connect with Stripe**
3. Complete the Stripe onboarding process
4. Return to Edrano and verify connection

Your portal is now ready to accept payments!

### Step 5: Invite Your First Client

1. Go to **Clients → Add Client**
2. Enter their name and email
3. Click **Send Invitation**

Your client will receive an email with login credentials.

### What's Next?

- [Creating Your First Service](/docs/first-service) - Detailed guide
- [Setting Up Payments](/docs/setup-payments) - Stripe configuration
- [Customizing Your Portal](/docs/customization) - Branding options
    `
  },
  
  'first-service': {
    title: 'Creating Your First Service',
    category: 'Getting Started',
    readTime: '3 min',
    content: `
## Creating Services That Sell

Services are the core of your client portal. Here's how to create services that convert.

### Service Structure

Each service includes:
- **Name**: Clear, descriptive title
- **Category**: Group related services together
- **Description**: Explain what's included
- **Pricing Tiers**: Monthly, yearly, or one-time options
- **Features**: Bulleted list of what's included

### Step-by-Step Guide

#### 1. Navigate to Services

From your admin dashboard, click **Services** in the sidebar, then **Add Service**.

#### 2. Choose a Category

Select an existing category or create a new one. Categories help clients find what they need.

**Good category examples:**
- Hosting Plans
- Maintenance
- Development
- Support

#### 3. Write a Compelling Description

Your description should:
- Start with the benefit to the client
- Explain what's included
- Be specific about deliverables
- Use simple, jargon-free language

**Example:**
> "Keep your WordPress site secure and up-to-date without lifting a finger. Our maintenance plan includes weekly updates, security monitoring, and monthly performance reports."

#### 4. Set Up Pricing

Create at least two pricing options:

| Tier | When to Use |
|------|-------------|
| Monthly | Lower commitment, easier to start |
| Annual | Offer 15-20% discount, better cash flow |
| One-time | For project-based work |

#### 5. List Features

Use short, scannable bullet points:

✅ **Good:** "Weekly plugin updates"
❌ **Bad:** "We will update all of your plugins every week to ensure compatibility"

### Pro Tips

1. **Start with 3-5 services** - Don't overwhelm clients
2. **Use consistent pricing** - Makes comparison easy
3. **Include images** - Services with images convert 40% better
4. **Review quarterly** - Update based on what sells
    `
  },
  
  'setup-payments': {
    title: 'Setting Up Payments',
    category: 'Getting Started',
    readTime: '5 min',
    content: `
## Connecting Stripe for Payments

Edrano uses Stripe to process payments securely. This guide walks you through the setup.

### Prerequisites

- A business email address
- Business bank account details
- Tax identification number (if applicable)

### Connecting Stripe

#### Step 1: Access Payment Settings

1. Log in to your Edrano dashboard
2. Click **Settings** in the sidebar
3. Select **Payment Settings**

#### Step 2: Start Stripe Onboarding

Click the **Connect with Stripe** button. You'll be redirected to Stripe.

#### Step 3: Create or Link Your Stripe Account

**New to Stripe?**
- Enter your email address
- Create a password
- Stripe will create an account for you

**Already have Stripe?**
- Click "Sign in to your Stripe account"
- Authorize Edrano to connect

#### Step 4: Complete Business Verification

Stripe requires:
- Business type (individual, company, etc.)
- Business address
- Bank account for payouts
- Tax information
- Identity verification (ID or passport)

#### Step 5: Return to Edrano

After completing Stripe onboarding, you'll be redirected back to Edrano. Your dashboard will show:

✅ **Stripe Connected**

### Testing Your Setup

1. Create a test service with a low price ($1)
2. Go to your public portal
3. Complete a purchase using Stripe test card: \`4242 4242 4242 4242\`
4. Verify the order appears in your dashboard

### Payout Schedule

By default, Stripe pays out:
- **US/UK/EU:** 2 business days after payment
- **Other regions:** 7 business days

You can customize this in your Stripe dashboard.

### Fees

| Fee Type | Amount |
|----------|--------|
| Stripe Processing | 2.9% + $0.30 |
| Edrano Fee | 0% (on direct plans) |

### Troubleshooting

**"Stripe connection failed"**
- Clear browser cache and try again
- Use a different browser
- Contact support if issue persists

**"Payments not appearing"**
- Check your Stripe dashboard for pending verifications
- Ensure your bank account is verified
    `
  },
  
  // Payments & Billing
  'stripe-connect': {
    title: 'Connecting Stripe',
    category: 'Payments & Billing',
    readTime: '5 min',
    content: `
## Stripe Integration Guide

Edrano integrates with Stripe Connect, allowing you to accept payments directly into your Stripe account.

### Why Stripe?

- **Trusted:** Used by millions of businesses worldwide
- **Secure:** PCI DSS Level 1 certified
- **Global:** Accept payments in 135+ currencies
- **Fast Payouts:** Get paid in 2-7 days

### Connection Process

1. **Navigate to Settings → Payments**
2. **Click "Connect with Stripe"**
3. **Complete Stripe Onboarding:**
   - Business information
   - Bank account for payouts
   - Identity verification
4. **Return to Edrano**

### What Happens After Connection

Once connected, Edrano will:
- Create checkout sessions for your services
- Process payments through your Stripe account
- Handle subscriptions and recurring billing
- Send payment receipts to clients

### Managing Payments in Stripe

You can always access your Stripe dashboard at [dashboard.stripe.com](https://dashboard.stripe.com) to:

- View transaction history
- Issue refunds
- Update payout settings
- Download tax reports

### Stripe Fees

Standard Stripe fees apply:

| Region | Fee |
|--------|-----|
| US | 2.9% + $0.30 |
| EU | 1.4% + €0.25 |
| UK | 1.4% + £0.20 |
| International | +1% |

Edrano does not add any additional fees on direct subscription plans.
    `
  },
  
  // White-Label Setup
  'custom-domain': {
    title: 'Custom Domain Setup',
    category: 'White-Label Setup',
    readTime: '5 min',
    content: `
## Setting Up Your Custom Domain

Make your client portal truly yours by using your own domain.

### Prerequisites

- Growth or Scale plan subscription
- Access to your domain's DNS settings
- Domain must be active and pointing to valid nameservers

### Step 1: Add Your Domain

1. Go to **Settings → Domain Settings**
2. Click **Add Custom Domain**
3. Enter your domain (e.g., \`portal.youragency.com\`)
4. Click **Verify Domain**

### Step 2: Configure DNS

Add the following DNS records at your domain registrar:

**For subdomain (recommended):**
\`\`\`
Type: CNAME
Name: portal (or your choice)
Value: custom.edrano.my
TTL: 3600
\`\`\`

**For apex domain:**
\`\`\`
Type: A
Name: @
Value: [Check your Edrano dashboard for IP]
TTL: 3600
\`\`\`

### Step 3: Wait for DNS Propagation

DNS changes can take up to 48 hours to propagate globally. Usually, it's much faster:
- Most changes: 15-30 minutes
- Worst case: 24-48 hours

### Step 4: Verify and Activate

Once DNS propagates:
1. Return to Domain Settings
2. Click **Verify DNS**
3. If successful, SSL will be automatically provisioned

### SSL Certificates

Edrano automatically provisions free SSL certificates via Let's Encrypt. This happens automatically after DNS verification.

### Troubleshooting

**"DNS not verified"**
- Double-check the CNAME record value
- Wait a few more hours for propagation
- Use [dnschecker.org](https://dnschecker.org) to verify

**"SSL provisioning failed"**
- Ensure there are no conflicting A/AAAA records
- Remove any existing SSL certificates from your registrar
- Contact support if issue persists

### Multiple Domains

Scale plan users can add up to 3 custom domains. Each domain can point to a different portal configuration.
    `
  },
  
  // Integrations
  'webhooks-overview': {
    title: 'Webhooks Overview',
    category: 'Integrations',
    readTime: '4 min',
    content: `
## Introduction to Webhooks

Webhooks allow Edrano to notify your systems in real-time when events occur.

### What Are Webhooks?

Webhooks are HTTP callbacks that send data to your server when specific events happen. Instead of constantly polling our API, you receive instant notifications.

### Available Events

| Event | Description |
|-------|-------------|
| \`order.created\` | New order placed |
| \`order.completed\` | Order payment successful |
| \`order.cancelled\` | Order was cancelled |
| \`client.created\` | New client registered |
| \`client.updated\` | Client details changed |
| \`service.renewed\` | Subscription renewed |
| \`service.expired\` | Subscription expired |
| \`ticket.created\` | New support ticket |
| \`ticket.replied\` | Ticket received reply |

### Setting Up Webhooks

1. Go to **Settings → Integrations → Webhooks**
2. Click **Add Webhook**
3. Enter your endpoint URL
4. Select events to subscribe to
5. Click **Save**

### Webhook Payload

All webhooks include:

\`\`\`json
{
  "event": "order.created",
  "timestamp": "2026-02-12T10:30:00Z",
  "data": {
    "id": "ord_abc123",
    "client_id": "cli_xyz789",
    "total": 99.00,
    "currency": "USD",
    "items": [...]
  },
  "signature": "sha256=..."
}
\`\`\`

### Verifying Webhooks

Always verify webhook signatures to ensure authenticity:

\`\`\`python
import hmac
import hashlib

def verify_signature(payload, signature, secret):
    expected = hmac.new(
        secret.encode(),
        payload.encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)
\`\`\`

### Best Practices

1. **Respond quickly** - Return 200 within 5 seconds
2. **Process async** - Queue heavy processing
3. **Handle duplicates** - Events may be sent multiple times
4. **Verify signatures** - Always validate authenticity
5. **Log everything** - Keep records for debugging
    `
  },
  
  // API Reference
  'api-auth': {
    title: 'API Authentication',
    category: 'API Reference',
    readTime: '4 min',
    content: `
## API Authentication Guide

Learn how to authenticate with the Edrano API.

### Authentication Methods

Edrano API uses **Bearer Token** authentication. Include your API key in the Authorization header of every request.

### Getting Your API Key

1. Log in to your Edrano dashboard
2. Go to **Settings → API Settings**
3. Click **Generate API Key**
4. Copy and securely store your key

⚠️ **Important:** API keys are shown only once. Store them securely.

### Making Authenticated Requests

Include the Authorization header in all requests:

\`\`\`bash
curl -X GET "https://api.edrano.my/v1/clients" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"
\`\`\`

### API Key Permissions

| Plan | Access Level |
|------|--------------|
| Starter | No API access |
| Growth | Read-only |
| Scale | Full access |

### Read-Only vs Full Access

**Read-Only (Growth plan):**
- GET requests only
- List and retrieve resources
- Cannot create, update, or delete

**Full Access (Scale plan):**
- All HTTP methods
- Create, update, delete resources
- Webhook management

### Error Responses

| Code | Meaning |
|------|---------|
| 401 | Missing or invalid API key |
| 403 | Insufficient permissions |
| 429 | Rate limit exceeded |

### Rate Limits

| Plan | Requests/Minute |
|------|-----------------|
| Growth | 60 |
| Scale | 300 |

### Security Best Practices

1. **Never expose keys in client-side code**
2. **Use environment variables** to store keys
3. **Rotate keys periodically** (every 90 days recommended)
4. **Use separate keys** for development and production
5. **Monitor API usage** in your dashboard
    `
  },
  
  // Security & Compliance
  'security-overview': {
    title: 'Security Overview',
    category: 'Security & Compliance',
    readTime: '5 min',
    content: `
## Edrano Security

Your data security is our top priority. Here's how we protect your information.

### Infrastructure Security

**Data Centers**
- Hosted on enterprise-grade cloud infrastructure
- Multiple availability zones for redundancy
- Physical security with 24/7 monitoring
- SOC 2 Type II compliant facilities

**Network Security**
- DDoS protection at network edge
- Web Application Firewall (WAF)
- Regular penetration testing
- Intrusion detection systems

### Data Protection

**Encryption**
- TLS 1.3 for all data in transit
- AES-256 encryption for data at rest
- Encrypted database connections
- Secure key management

**Access Control**
- Role-based access control (RBAC)
- Multi-factor authentication (optional)
- Session management with secure tokens
- Audit logs for all actions

### Tenant Isolation

Each tenant's data is completely isolated:

- **Separate databases** for each tenant
- **No shared data** between tenants
- **Logical isolation** at application layer
- **Encrypted tenant identifiers**

### Compliance

| Standard | Status |
|----------|--------|
| GDPR | Compliant |
| SOC 2 Type II | Ready |
| PCI DSS | Via Stripe |
| CCPA | Compliant |

### Incident Response

We maintain a comprehensive incident response plan:

1. **Detection** - Automated monitoring and alerts
2. **Response** - 24/7 security team
3. **Communication** - Timely customer notification
4. **Recovery** - Rapid restoration procedures
5. **Review** - Post-incident analysis

### Your Responsibilities

While we secure the platform, you should:

- Use strong, unique passwords
- Enable two-factor authentication
- Keep your API keys secure
- Review access logs regularly
- Report suspicious activity

### Security Contact

Report vulnerabilities: security@edrano.my
    `
  },
  
  'gdpr': {
    title: 'GDPR Compliance',
    category: 'Security & Compliance',
    readTime: '6 min',
    content: `
## GDPR Compliance Guide

Edrano is fully compliant with the General Data Protection Regulation (GDPR).

### Your Rights Under GDPR

As a user, you have the right to:

| Right | Description |
|-------|-------------|
| **Access** | Request a copy of your data |
| **Rectification** | Correct inaccurate data |
| **Erasure** | Request deletion of your data |
| **Portability** | Export data in machine-readable format |
| **Object** | Object to certain processing |
| **Withdraw Consent** | Withdraw consent at any time |

### Data We Collect

**Account Data:**
- Name and email address
- Company name and phone number
- Billing information (processed by Stripe)

**Usage Data:**
- Login history
- Feature usage analytics
- Support ticket communications

**Client Data:**
- Your clients' information (you are the data controller)
- Orders and transactions
- Support history

### Data Processing

We process data for:
- Providing our services
- Billing and payments
- Customer support
- Product improvement
- Legal compliance

### Your Clients' Data

When you use Edrano, you become a **data controller** for your clients' data. You must:

1. Have a privacy policy
2. Obtain proper consent
3. Respond to data requests
4. Report breaches promptly

### Exercising Your Rights

**To export your data:**
1. Go to Settings → Privacy
2. Click "Export My Data"
3. Download the JSON file

**To delete your account:**
1. Go to Settings → Privacy
2. Click "Request Account Deletion"
3. Confirm via email

### Data Retention

| Data Type | Retention Period |
|-----------|-----------------|
| Account data | Until deletion requested |
| Financial records | 7 years (legal requirement) |
| Logs | 90 days |
| Backups | 30 days |

### Contact

Data Protection Officer: privacy@edrano.my
    `
  }
};

// Sidebar categories for navigation
const DOC_CATEGORIES = [
  {
    name: 'Getting Started',
    articles: ['quick-start', 'first-service', 'setup-payments']
  },
  {
    name: 'Payments & Billing',
    articles: ['stripe-connect']
  },
  {
    name: 'White-Label Setup',
    articles: ['custom-domain']
  },
  {
    name: 'Integrations',
    articles: ['webhooks-overview']
  },
  {
    name: 'API Reference',
    articles: ['api-auth']
  },
  {
    name: 'Security & Compliance',
    articles: ['security-overview', 'gdpr']
  }
];

// ============================================
// ARTICLE PAGE COMPONENT
// ============================================

export default function DocArticle() {
  const { slug } = useParams();
  const article = ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F7F4EF]">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-stone-900 mb-4">Article Not Found</h1>
          <p className="text-stone-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/docs">
            <Button className="bg-stone-900 text-white rounded-full">
              Back to Documentation
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EF]">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              </Link>
              <span className="text-stone-300">|</span>
              <Link to="/docs" className="text-sm font-medium text-stone-600 hover:text-stone-900">
                Docs
              </Link>
            </div>
            <Link to="/register">
              <Button size="sm" className="bg-stone-900 text-white rounded-full">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-6">
              {DOC_CATEGORIES.map((category, catIndex) => (
                <div key={catIndex}>
                  <h4 className="font-medium text-stone-900 mb-2">{category.name}</h4>
                  <ul className="space-y-1">
                    {category.articles.map((articleSlug) => {
                      const art = ARTICLES[articleSlug];
                      if (!art) return null;
                      return (
                        <li key={articleSlug}>
                          <Link
                            to={`/docs/${articleSlug}`}
                            className={`block py-1.5 px-3 rounded-lg text-sm transition-colors ${
                              slug === articleSlug
                                ? 'bg-stone-900 text-white'
                                : 'text-stone-600 hover:bg-stone-100'
                            }`}
                          >
                            {art.title}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-stone-500 mb-6">
              <Link to="/docs" className="hover:text-stone-900">Docs</Link>
              <ChevronRight className="w-4 h-4" />
              <span>{article.category}</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-stone-900">{article.title}</span>
            </div>

            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-stone-900 mb-3">{article.title}</h1>
              <div className="flex items-center gap-4 text-sm text-stone-500">
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {article.category}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {article.readTime} read
                </span>
              </div>
            </div>

            {/* Article Content */}
            <article 
              className="prose prose-stone max-w-none
                prose-headings:font-bold prose-headings:text-stone-900
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-2
                prose-p:text-stone-600 prose-p:leading-relaxed
                prose-li:text-stone-600
                prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline
                prose-code:bg-stone-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                prose-pre:bg-stone-900 prose-pre:text-stone-100
                prose-table:text-sm
                prose-th:bg-stone-100 prose-th:px-4 prose-th:py-2
                prose-td:px-4 prose-td:py-2 prose-td:border-b prose-td:border-stone-100"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(article.content) }}
              data-testid="article-content"
            />

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-stone-200">
              <Link 
                to="/docs"
                className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-stone-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-stone-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-stone-500 hover:text-stone-900">Privacy</Link>
              <Link to="/terms" className="text-sm text-stone-500 hover:text-stone-900">Terms</Link>
              <a href="mailto:support@edrano.my" className="text-sm text-stone-500 hover:text-stone-900">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Simple markdown to HTML converter
function formatMarkdown(content) {
  return content
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Tables
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.every(c => c.trim().match(/^-+$/))) {
        return ''; // Skip separator row
      }
      const isHeader = match.includes('---') ? false : true;
      const tag = isHeader ? 'td' : 'td';
      return `<tr>${cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('')}</tr>`;
    })
    // Unordered lists
    .replace(/^- (.*)$/gim, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    // Ordered lists
    .replace(/^\d+\. (.*)$/gim, '<li>$1</li>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    // Line breaks
    .replace(/\n/g, '<br>')
    // Wrap in paragraph
    .replace(/^(.+)$/gm, (match) => {
      if (match.startsWith('<')) return match;
      return `<p>${match}</p>`;
    })
    // Clean up
    .replace(/<p><\/p>/g, '')
    .replace(/<p><br><\/p>/g, '')
    .replace(/<br><h/g, '<h')
    .replace(/<\/h(\d)><br>/g, '</h$1>');
}
